import {
  AvailabilitiesResponse,
  RateData,
  ExtraConfig,
  AvailabilityData,
  TimeSlotAvailabilityMap,
} from "@/types/api/availabilities";
import {
  ActivityPricingCategory,
  BookingDate,
  PriceTier,
  RateOption,
  RateOptionCategoryPricing,
  TimeSlotData,
} from "@/types/api/group-tour-details";
import { normalizeAccommodationSet } from "./accommodation-utils";
import {
  getMarketplaceSlotRemainingCapacity,
  isMarketplaceSlotAvailable,
} from "./marketplace-availability-utils";

function parseMarketplaceRatesFromTimeSlot(
  rates: { [rateId: string]: RateData } | undefined,
  pricingCategories: ActivityPricingCategory[]
): { rateOptions: RateOption[]; detectedCurrency: string } {
  const rateOptions: RateOption[] = [];
  let detectedCurrency = "INR";

  if (!rates) {
    return { rateOptions, detectedCurrency };
  }

  Object.entries(rates).forEach(([rateId, rate]: [string, RateData]) => {
    const ratesMap: { [key: string]: number } = {};
    const pricePerCategoryUnit: { [key: string]: PriceTier[] } = {};
    let tieredPricingEnabled = rate.tieredPricingEnabled ?? false;

    if (rate.pricePerCategoryUnit) {
      Object.entries(rate.pricePerCategoryUnit).forEach(
        ([catId, priceInfo]) => {
          const category = pricingCategories.find(
            (pc) => pc.id.toString() === catId
          );
          if (!category || !priceInfo) {
            return;
          }

          const tiers = priceInfo.map((tier) => ({
            amount: tier.amount,
            currency: tier.currency,
            minParticipantsRequired: tier.minParticipantsRequired,
            maxParticipantsRequired: tier.maxParticipantsRequired ?? null,
          }));

          if (tiers.length > 0) {
            pricePerCategoryUnit[catId] = tiers;
            ratesMap[catId] = tiers[0].amount;
            detectedCurrency = tiers[0].currency;
            tieredPricingEnabled = true;
          }
        }
      );
    }

    const pickupPricePerCategory: RateOptionCategoryPricing = {};
    if (rate.pickupPricePerCategoryUnit) {
      Object.entries(rate.pickupPricePerCategoryUnit).forEach(
        ([catId, priceInfo]) => {
          pickupPricePerCategory[catId] = {
            amount: priceInfo.amount,
            currency: priceInfo.currency,
          };
        }
      );
    }

    const dropoffPricePerCategory: RateOptionCategoryPricing = {};
    if (rate.dropoffPricePerCategoryUnit) {
      Object.entries(rate.dropoffPricePerCategoryUnit).forEach(
        ([catId, priceInfo]) => {
          dropoffPricePerCategory[catId] = {
            amount: priceInfo.amount,
            currency: priceInfo.currency,
          };
        }
      );
    }

    const extraPricing: RateOption["extraPricing"] = {};
    if (rate.extraConfigs) {
      Object.entries(rate.extraConfigs).forEach(
        ([extraId, ec]: [string, ExtraConfig]) => {
          extraPricing[extraId] = {
            pricingType: ec.pricingType,
            pricedPerPerson: ec.pricedPerPerson,
            flatPrice: null,
            pricePerCategory: {},
          };

          if (rate.extraPricePerUnit && rate.extraPricePerUnit[extraId]) {
            const flatPriceData = rate.extraPricePerUnit[extraId];
            extraPricing[extraId].flatPrice = {
              amount: flatPriceData.amount,
              currency: flatPriceData.currency,
            };
          }

          if (
            rate.extraPricePerCategoryUnit &&
            rate.extraPricePerCategoryUnit[extraId]
          ) {
            const categoryPrices =
              rate.extraPricePerCategoryUnit[extraId].prices;
            if (categoryPrices) {
              Object.entries(categoryPrices).forEach(([catId, priceInfo]) => {
                extraPricing[extraId].pricePerCategory[catId] = {
                  amount: priceInfo.amount,
                  currency: priceInfo.currency,
                };
              });
            }
          }
        }
      );
    }

    rateOptions.push({
      id: rateId,
      title: rate.title,
      description: rate.description ?? "",
      rates: ratesMap,
      pricedPerPerson: rate.pricedPerPerson,
      minPerBooking: rate.minPerBooking,
      tieredPricingEnabled,
      pricePerCategoryUnit,
      startTimeIds: rate.startTimeIds || [],
      accommodationSet: normalizeAccommodationSet(rate.accommodationSet),
      cancellation_policy: rate.cancellationPolicy || [],
      pickupSelectionType: rate.pickupSelectionType || "UNAVAILABLE",
      dropoffSelectionType: rate.dropoffSelectionType || "UNAVAILABLE",
      drop_off_availability: rate.dropoffSelectionType,
      extras: rate.extraConfigs
        ? Object.entries(rate.extraConfigs).map(
            ([extraId, ec]: [string, ExtraConfig]) => ({
              id: parseInt(extraId),
              activityExtraId: parseInt(extraId),
              selectionType: ec.selectionType,
              pricingType: ec.pricingType,
              pricedPerPerson: ec.pricedPerPerson,
            })
          )
        : [],
      pickupPricingType: rate.pickupPricingType || "PRICED_SEPARATELY",
      pickupPricedPerPerson: rate.pickupPricedPerPerson ?? true,
      pickupPrice: rate.pickupPrice
        ? {
            amount: rate.pickupPrice.amount,
            currency: rate.pickupPrice.currency,
          }
        : null,
      pickupPricePerCategory,
      dropoffPricingType: rate.dropoffPricingType || "PRICED_SEPARATELY",
      dropoffPricedPerPerson: rate.dropoffPricedPerPerson ?? false,
      dropoffPrice: rate.dropoffPrice
        ? {
            amount: rate.dropoffPrice.amount,
            currency: rate.dropoffPrice.currency,
          }
        : null,
      dropoffPricePerCategory,
      extraPricing,
    });
  });

  return { rateOptions, detectedCurrency };
}

export function mapMarketplaceAvailabilitiesToBookingDates(
  availabilities: AvailabilitiesResponse,
  pricingCategories: ActivityPricingCategory[]
): BookingDate[] {
  const dates: BookingDate[] = [];

  const availabilityData = availabilities?.data ?? {};
  if (!availabilityData || Object.keys(availabilityData).length === 0) {
    return dates;
  }

  Object.entries(availabilityData).forEach(
    ([dateKey, timeSlotMap]: [string, TimeSlotAvailabilityMap]) => {
      if (!timeSlotMap || typeof timeSlotMap !== "object") {
        return;
      }

      const timeSlots: { [time: string]: TimeSlotData } = {};
      let detectedCurrency = "INR";
      let anyAvailable = false;

      const timeKeys = Object.keys(timeSlotMap).sort();

      timeKeys.forEach((timeKey) => {
        const slotData: AvailabilityData = timeSlotMap[timeKey];

        if (!slotData || typeof slotData !== "object") {
          return;
        }

        const { rateOptions, detectedCurrency: slotCurrency } =
          parseMarketplaceRatesFromTimeSlot(
            slotData.rates,
            pricingCategories
          );

        detectedCurrency = slotCurrency;

        const remainingCapacity = getMarketplaceSlotRemainingCapacity(slotData);
        const slotAvailable = isMarketplaceSlotAvailable(slotData);

        if (slotAvailable) {
          anyAvailable = true;
        }

        timeSlots[timeKey] = {
          startTime: slotData.startTime?.trim() ?? "",
          startTimeId: slotData.startTimeId,
          rate_options: rateOptions,
          available: slotAvailable,
          isBookingAllowed: slotData.is_booking_allowed,
          availabilityCount:
            remainingCapacity ?? slotData.availabilityCount,
          unlimitedAvailability: slotData.unlimitedAvailability,
          defaultRateId: slotData.defaultRateId,
          minParticipants: slotData.minParticipants,
          minParticipantsToBookNow: slotData.minParticipantsToBookNow,
        };
      });

      const firstTimeKey = timeKeys[0];
      const firstTimeSlot = firstTimeKey ? timeSlots[firstTimeKey] : null;

      dates.push({
        date: dateKey,
        available: anyAvailable,
        isBookingAllowed:
          firstTimeSlot?.isBookingAllowed ??
          (firstTimeKey ? timeSlotMap[firstTimeKey]?.is_booking_allowed : undefined),
        currency: detectedCurrency,
        timeSlots,
        rate_options: firstTimeSlot?.rate_options || [],
        startTimeId: firstTimeSlot?.startTimeId,
        availabilityCount: firstTimeSlot?.availabilityCount,
        unlimitedAvailability: firstTimeSlot?.unlimitedAvailability,
        defaultRateId: firstTimeSlot?.defaultRateId,
        minParticipants: firstTimeSlot?.minParticipants,
        minParticipantsToBookNow: firstTimeSlot?.minParticipantsToBookNow,
      });
    }
  );

  return dates;
}
